import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import type { LucideIcon } from "lucide-react";
import { Icon } from "./Icon";
import { cx } from "./cx";
import { FIELD_HEIGHT, FieldLabel, FieldNote, type FieldSize } from "./fieldParts";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  /** Helper text under the field. */
  hint?: string;
  /** Error message. Replaces the hint and reddens the border. Name the next step. */
  error?: string;
  /** Lucide icon, leading. */
  icon?: LucideIcon;
  /** Static leading text, e.g. "₱". */
  prefix?: string;
  /** Trailing text, e.g. "kg", "mm", or a small control such as a password's Show/Hide. */
  suffix?: ReactNode;
  size?: FieldSize;
  /** Monospace value. Use for SKUs, part numbers, order refs. */
  mono?: boolean;
  /** The inner input, for moving focus to it. */
  ref?: Ref<HTMLInputElement>;
}

/** Text field with label, optional leading icon, hint and error. */
export function Input({ label, hint, error, icon, prefix, suffix, size = "md", mono, id, className, disabled, ...rest }: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const noteId = `${inputId}-note`;

  return (
    <label htmlFor={inputId} className={cx("block", className)}>
      {label && <FieldLabel required={rest.required}>{label}</FieldLabel>}
      <span
        className={cx(
          "flex items-center gap-2 rounded-sm border px-3 inset-shadow-default [transition:var(--transition-control)]",
          FIELD_HEIGHT[size],
          disabled ? "bg-paper-2" : "bg-surface-card",
          error ? "border-danger-500" : "border-border-default focus-within:border-green-700",
          "focus-within:shadow-focus",
        )}
      >
        {icon && <Icon icon={icon} size={18} className="text-ink-400" />}
        {prefix && <span className="type-body text-text-muted">{prefix}</span>}
        <input
          id={inputId}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? noteId : undefined}
          className={cx(
            "min-w-0 flex-1 border-none bg-transparent text-text-strong outline-none",
            mono ? "type-sku tracking-wide" : "type-body",
            "[font-size:var(--text-base)] disabled:cursor-not-allowed",
          )}
          {...rest}
        />
        {suffix && <span className="whitespace-nowrap type-small text-text-muted">{suffix}</span>}
      </span>
      <FieldNote id={noteId} error={error} hint={hint} />
    </label>
  );
}
